import { generateDemoData } from './generateDemoData';
import { finalizeDataLoad } from '@/utils/assetModel';

const TABLE_ORDER = [
  'assets',
  'status_history',
  'locations',
  'drawings',
  'status_templates',
  'teams',
  'users',
  'team_users',
  'categories',
  'item_templates',
  'current_user',
  'permission_rules',
  'company',
];

const keyById = (rows) => {
  const result = {};
  rows.forEach((row) => {
    result[row.Id] = row;
  });
  return result;
};

const groupLocationsByDrawing = (locations) => {
  const result = {};
  locations.forEach((location) => {
    if (!result[location.DrawingId]) {
      result[location.DrawingId] = [];
    }
    result[location.DrawingId].push(location);
  });
  return result;
};

// The demo set is large enough that a single JSON blob per table is slow to
// serialise; chunk the assets table.
const CHUNK_SIZE = 2000;

const insertChunked = async (duckDBService, rows, table) => {
  if (rows.length <= CHUNK_SIZE) {
    return duckDBService.insertJSONToDuckDB(rows, table);
  }
  let ok = true;
  for (let i = 0; i < rows.length; i += CHUNK_SIZE) {
    const chunk = rows.slice(i, i + CHUNK_SIZE);
    const inserted = i === 0
      ? await duckDBService.insertJSONToDuckDB(chunk, table)
      : await duckDBService.appendJSONToDuckDB(chunk, table);
    ok = ok && inserted;
  }
  return ok;
};

export async function loadDemoData(store) {
  const duckDBService = store.getters.getDuckDBService;
  if (!duckDBService) {
    console.error('[demo] DuckDB service unavailable, cannot load demo data');
    return false;
  }

  const data = generateDemoData();

  for (const table of TABLE_ORDER) {
    const rows = data[table];
    if (!rows || rows.length === 0) {
      console.warn(`[demo] Skipping empty table ${table}`);
      continue;
    }
    const inserted = await insertChunked(duckDBService, rows, table);
    if (!inserted) {
      console.error(`[demo] Failed to insert demo rows into ${table}`);
    }
  }

  // read_json_auto infers BIGINT for whole numbers, which surfaces as a JS
  // BigInt in the sort comparators of the carried chart components.
  await duckDBService.conn.loadData('ALTER TABLE status_templates ALTER COLUMN Sort TYPE INTEGER;');

  store.commit('setStatusTemplatesObj', keyById(data.status_templates));
  store.commit('setLocationsObj', keyById(data.locations));
  store.commit('setTeamsObj', keyById(data.teams));
  store.commit('setUsersObj', keyById(data.users));
  store.commit('setCategoriesObj', keyById(data.categories));
  store.commit('setCategories', data.categories);
  store.commit('setItemTemplatesObj', keyById(data.item_templates));
  store.commit('setPermissionRulesObj', keyById(data.permission_rules));
  store.commit('setDrawings', data.drawings);
  store.commit('setDrawingsObj', keyById(data.drawings));
  store.commit('setDrawingLocationsObj', groupLocationsByDrawing(data.locations));

  const teamUsersObj = {};
  const userTeamsObj = {};
  data.team_users.forEach((link) => {
    if (!teamUsersObj[link.TeamId]) teamUsersObj[link.TeamId] = [];
    teamUsersObj[link.TeamId].push(link);
    if (!userTeamsObj[link.UserId]) userTeamsObj[link.UserId] = [];
    userTeamsObj[link.UserId].push(link);
  });
  store.commit('setTeamUsersObj', teamUsersObj);
  store.commit('setUserTeamsObj', userTeamsObj);

  // Builds asset_items / unique_assets, seeds the cross-filter and flips
  // dataLoaded - the same tail the live load path runs.
  await finalizeDataLoad(store);

  return true;
}
